/**
 * Matrice d'activation des modules par ligue.
 * Source de vérité unique consommée par la nav (Header), les gardes de routes
 * (AppRouter/ProtectedRoute), le résolveur de landing route et le panneau
 * Settings → Modules. La config vit dans league_settings_versions.config.modules ;
 * toute absence (ligue jamais configurée, clé manquante) vaut « activé ».
 */

export type ModuleKey =
  | "planning"
  | "calendar_generation"
  | "rankings"
  | "disciplinary"
  | "accounting"
  | "payments_treasury"
  | "officials"
  | "club_registry"
  | "stadiums_audits"
  | "mission_orders"
  | "news"
  | "media_accreditation";

export type ModuleConfigValue =
  | boolean
  | { enabled: boolean; features?: Record<string, boolean> };

export type LeagueModulesConfig = Partial<Record<ModuleKey, ModuleConfigValue>>;

export interface ModuleFeatureDef {
  key: string;
  labelKey: string; // i18n key, namespace "settings"
}

export interface ModuleDef {
  key: ModuleKey;
  labelKey: string; // i18n key, namespace "settings"
  features: ModuleFeatureDef[];
}

const f = (key: string): ModuleFeatureDef => ({
  key,
  labelKey: `modules_matrix.features.${key}`,
});

export const MODULE_REGISTRY: ModuleDef[] = [
  {
    key: "planning",
    labelKey: "modules_matrix.modules.planning",
    features: [
      f("week"), f("urgents"), f("to_plan"), f("all_matches"),
      f("calendar_views"), f("game_day_focus"), f("faf_audit"),
      f("engagements"),
    ],
  },
  {
    key: "calendar_generation",
    labelKey: "modules_matrix.modules.calendar_generation",
    // Pas de feature « library » : /calendar (index) EST le module. Un
    // interrupteur pour la page d'accueil du module mentirait — couper la
    // bibliothèque sans couper le module laisserait une coquille vide.
    features: [f("config"), f("draw"), f("auto_generate"), f("manual_planner")],
  },
  { key: "rankings", labelKey: "modules_matrix.modules.rankings", features: [] },
  {
    key: "disciplinary",
    labelKey: "modules_matrix.modules.disciplinary",
    features: [
      f("entry_by_match"), f("player_sanctions"), f("team_sanctions"),
      f("sessions_pv"), f("archive"), f("reports"), f("payments"),
    ],
  },
  {
    key: "accounting",
    labelKey: "modules_matrix.modules.accounting",
    features: [f("kanban"), f("closure")],
  },
  {
    key: "payments_treasury",
    labelKey: "modules_matrix.modules.payments_treasury",
    features: [f("finances_dashboard"), f("virements"), f("etats")],
  },
  {
    key: "officials",
    labelKey: "modules_matrix.modules.officials",
    features: [f("officials"), f("requests"), f("duplicates"), f("history"), f("import")],
  },
  {
    key: "club_registry",
    labelKey: "modules_matrix.modules.club_registry",
    features: [f("clubs"), f("players"), f("staff"), f("transfers")],
  },
  {
    key: "stadiums_audits",
    labelKey: "modules_matrix.modules.stadiums_audits",
    features: [f("stadiums"), f("audit_planning")],
  },
  {
    key: "mission_orders",
    labelKey: "modules_matrix.modules.mission_orders",
    features: [f("manual_missions"), f("pdf_batches")],
  },
  { key: "news", labelKey: "modules_matrix.modules.news", features: [] },
  {
    key: "media_accreditation",
    labelKey: "modules_matrix.modules.media_accreditation",
    features: [
      f("overview"), f("demands"), f("matches"), f("accreditations"),
      f("accounts"), f("organizations"), f("league_access"), f("logs"),
      f("designer"),
    ],
  },
];

export interface ResolvedModules {
  isModuleEnabled(key: ModuleKey): boolean;
  isFeatureEnabled(key: ModuleKey, feature: string): boolean;
}

export function resolveModules(
  config: LeagueModulesConfig | null | undefined,
): ResolvedModules {
  const cfg = config ?? {};
  const moduleEnabled = (key: ModuleKey): boolean => {
    const value = cfg[key];
    // La config vient de jsonb : null, {} ou une clé manquante sont tous plausibles
    // et doivent tomber côté « activé » plutôt que lever ou désactiver.
    if (value === undefined || value === null) return true; // absence = activé
    if (typeof value === "boolean") return value;
    return value.enabled !== false; // `enabled` absent = activé
  };
  return {
    isModuleEnabled: moduleEnabled,
    isFeatureEnabled(key: ModuleKey, feature: string): boolean {
      if (!moduleEnabled(key)) return false;
      const value = cfg[key];
      if (value === undefined || value === null || typeof value === "boolean") {
        return true;
      }
      return value.features?.[feature] ?? true; // absence = activé
    },
  };
}

export const ALL_MODULES_ENABLED: ResolvedModules = resolveModules(undefined);
